import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { StatCard, PageHeader } from "@/components/StatCard";
import OpportunityCharts from "@/components/OpportunityCharts";
import { DeityModeCard } from "@/components/DeityModeCard";
import { CommissionCard } from "@/components/CommissionCard";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Rocket, Users2, Mail, Voicemail, MessageSquareReply, HeartHandshake,
  Trophy, DollarSign, ArrowRight, Clock, AlertTriangle, MailOpen, QrCode,
} from "lucide-react";

const money = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const statusColor = {
  completed: "bg-emerald-100 text-emerald-800",
  processing: "bg-blue-100 text-blue-800",
  paid: "bg-blue-100 text-blue-800",
  awaiting_payment: "bg-amber-100 text-amber-800",
  twin_access_required: "bg-orange-100 text-orange-800",
  failed: "bg-red-100 text-red-800",
};

export default function Dashboard() {
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [runs, setRuns] = useState([]);
  const [price, setPrice] = useState(60);
  const [expiring, setExpiring] = useState([]);

  useEffect(() => {
    api.get("/dashboard").then((r) => setStats(r.data));
    api.get("/runs").then((r) => setRuns(r.data.slice(0, 5)));
    api.get("/config").then((r) => setPrice(r.data.price));
    api.get("/prospects/expiring?days=2").then((r) => setExpiring(r.data)).catch(() => {});
  }, []);

  const cards = [
    { label: "Prospecting Runs", value: stats?.total_runs ?? "—", icon: Rocket, accent: "bg-slate-900 text-amber-400", testid: "stat-runs" },
    { label: "New Prospects", value: stats?.new_prospects ?? "—", icon: Users2, accent: "bg-blue-100 text-blue-700", testid: "stat-new-prospects" },
    { label: "Emails Sent", value: stats?.emails_sent ?? "—", icon: Mail, accent: "bg-indigo-100 text-indigo-700", testid: "stat-emails" },
    { label: "Emails Viewed", value: stats?.emails_viewed ?? "—", icon: MailOpen, accent: "bg-sky-100 text-sky-700", testid: "stat-emails-viewed" },
    { label: "Postcard QR Scans", value: stats ? `${stats.postcard_qr_scans} of ${stats.postcards_sent}` : "—", icon: QrCode, accent: "bg-teal-100 text-teal-700", testid: "stat-qr-scans" },
    { label: "Voicemails Sent", value: stats?.voicemails_sent ?? "—", icon: Voicemail, accent: "bg-purple-100 text-purple-700", testid: "stat-voicemails" },
    { label: "Responses", value: stats?.responses ?? "—", icon: MessageSquareReply, accent: "bg-cyan-100 text-cyan-700", testid: "stat-responses" },
    { label: "Interested Leads", value: stats?.interested_leads ?? "—", icon: HeartHandshake, accent: "bg-rose-100 text-rose-700", testid: "stat-interested" },
    { label: "Converted Leads", value: stats?.converted_leads ?? "—", icon: Trophy, accent: "bg-emerald-100 text-emerald-700", testid: "stat-converted" },
    { label: "Total Spent", value: money(stats?.total_spent), icon: DollarSign, accent: "bg-amber-100 text-amber-700", testid: "stat-spent" },
  ];

  return (
    <div>
      <PageHeader title="Dashboard" subtitle="Your private prospecting command center." />

      {/* Hero CTA */}
      <Card className="relative mb-8 overflow-hidden rounded-2xl border-0 navy-panel p-6 sm:p-8 text-white">
        <img src="https://static.prod-images.emergentagent.com/jobs/0d7ee199-4c9a-4ee4-a802-6a8f9b597807/images/7ea03af674084eab559aaffd7b7a3fb5eef4b15ad8fda0fa4be0fc2bcf361c27.jpeg" alt="" aria-hidden="true" className="pointer-events-none absolute inset-y-0 right-0 z-0 h-full w-1/2 object-cover opacity-15 [mask-image:linear-gradient(to_right,transparent,black)]" />
        <div className="relative z-10 flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <Badge className="bg-amber-400/15 text-amber-300 border-amber-400/30 hover:bg-amber-400/20">
              Ready when you are
            </Badge>
            <h2 className="mt-3 font-display text-2xl sm:text-3xl font-extrabold">
              Start New Prospecting Run
            </h2>
            <p className="mt-1.5 max-w-lg text-slate-300 text-sm">
              Select your mortgage type, target locations and criteria. Our research
              agent finds qualified prospects and kicks off compliant outreach.
            </p>
          </div>
          <Button data-testid="start-prospecting-run-btn" onClick={() => nav("/new-run")}
            className="gold-cta border-0 h-14 px-8 text-base font-bold shrink-0">
            Start New Prospecting Run — {money(price)}
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </Card>

      <CommissionCard />

      <DeityModeCard />

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {cards.map((c) => <StatCard key={c.label} {...c} />)}
      </div>

      {expiring.length > 0 && (
        <Card className="mt-8 border-amber-200 bg-amber-50/40 p-5" data-testid="expiring-soon-card">
          <div className="mb-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-600" />
              <h3 className="font-display text-lg font-bold text-slate-900">Expiring Soon</h3>
              <Badge className="border-0 bg-amber-200 text-amber-900">{expiring.length}</Badge>
            </div>
            <Button variant="ghost" size="sm" className="text-amber-800" onClick={() => nav("/prospects?expiring=1")} data-testid="view-expiring-btn">
              View all <ArrowRight className="ml-1 h-4 w-4" />
            </Button>
          </div>
          <p className="mb-3 text-xs text-slate-500">These leads release to the team within 2 days unless you work them — add a note, change status, or reach out.</p>
          <div className="divide-y divide-amber-100">
            {expiring.slice(0, 5).map((p) => (
              <div key={p.id} className="flex items-center justify-between gap-3 py-2" data-testid={`expiring-row-${p.id}`}>
                <div className="min-w-0">
                  <div className="truncate font-semibold text-slate-900">{p.name || "Unknown"}</div>
                  <div className="truncate text-xs text-slate-500">{[p.city, p.state].filter(Boolean).join(", ")} · {p.property_address}</div>
                </div>
                <Badge className="shrink-0 border-0 bg-amber-100 text-amber-800">{p.opportunity_type || "Lead"}</Badge>
              </div>
            ))}
          </div>
        </Card>
      )}

      <div className="mt-8" data-testid="mlo-analytics">
        <h3 className="mb-3 font-display text-lg font-bold text-slate-900">Lead Analytics</h3>
        <OpportunityCharts breakdown={stats?.opportunity_breakdown} runs={stats?.runs_breakdown} />
      </div>

      {/* Recent runs */}
      <div className="mt-8">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="font-display text-lg font-bold text-slate-900">Recent Runs</h3>
          <Button variant="ghost" size="sm" onClick={() => nav("/prospects")} className="text-slate-600">
            View prospects <ArrowRight className="ml-1 h-4 w-4" />
          </Button>
        </div>
        <Card className="divide-y divide-slate-100">
          {runs.length === 0 && (
            <div className="p-8 text-center text-sm text-slate-500">
              No prospecting runs yet. Start your first run above.
            </div>
          )}
          {runs.map((r) => (
            <div key={r.id} className="flex items-start justify-between gap-3 p-4" data-testid={`run-row-${r.id}`}>
              <div className="min-w-0">
                <div className="font-semibold text-slate-900 truncate">{r.prospect_type}</div>
                <div className="mt-0.5 flex items-center gap-2 text-xs text-slate-500">
                  <Clock className="h-3.5 w-3.5" />
                  {new Date(r.created_at).toLocaleString()}
                  <span>· {r.prospects_generated}/{r.prospects_requested} prospects</span>
                </div>
                {r.status === "completed" && r.prospects_generated > 0 && (
                  <div className="mt-1 text-xs text-slate-500" data-testid={`run-coverage-${r.id}`}>
                    Contacts: {r.contacts_with_phone ?? 0}/{r.prospects_generated} phone · {r.contacts_with_email ?? 0}/{r.prospects_generated} email
                  </div>
                )}
                {r.target_type && r.properties_checked > 0 && (
                  <div className="mt-1 text-xs text-slate-500" data-testid={`run-match-${r.id}`}>
                    {r.status === "processing" ? "Searching… checked" : "Checked"} {r.properties_checked} properties · {r.prospects_generated} matched {r.target_type}
                    {r.search_area === "Entire state of Florida" && " · all of Florida"}
                  </div>
                )}
                {r.status === "failed" && r.error_detail && (
                  <div className="mt-1.5 text-xs text-red-600" data-testid={`run-error-${r.id}`}>{r.error_detail}</div>
                )}
              </div>
              <div className="flex shrink-0 flex-col items-end gap-1">
                <Badge className={`${statusColor[r.status] || "bg-slate-100 text-slate-700"} border-0 capitalize`}>
                  {r.status.replace(/_/g, " ")}
                </Badge>
                {r.refunded && <Badge className="bg-emerald-100 text-emerald-800 border-0" data-testid={`run-refunded-${r.id}`}>Refunded</Badge>}
              </div>
            </div>
          ))}
        </Card>
      </div>
    </div>
  );
}
